import {
  doc,
  collection,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../../firebase";
import {
  FullMenuPageData,
  BaseItem,
  MenuCategory,
  Bundle,
} from "../../models/fullMenuPageData"; // import your TS interfaces
import { getCached } from "./firestoreCache";

const MENU_SUBCOLS = {
  baseItems: "baseItems",
  categories: "menuCategories",
  bundles: "bundles",
};

export const menuService = {
  /**
   * Fetch the full menu for a given restaurant.
   */
  async fetchFullMenu(restaurantId: string): Promise<FullMenuPageData> {
    const root = doc(firestore, "restaurants", restaurantId);

    // Kick off all fetches in parallel
    const [baseSnap, catSnap, bundleSnap] = await Promise.all([
      getDocs(collection(root, MENU_SUBCOLS.baseItems)),
      getDocs(collection(root, MENU_SUBCOLS.categories)),
      getDocs(collection(root, MENU_SUBCOLS.bundles)),
    ]);

    const baseItems = baseSnap.docs.map(d => d.data() as BaseItem);
    const categories = catSnap.docs.map(d => d.data() as MenuCategory);
    const bundles = bundleSnap.docs.map(d => d.data() as Bundle);

    return {
      baseItems,
      menu: {
        categories,
        bundles,
      },
    };
  },

  async fetchFullMenuCached(restaurantId: string, ttlMs = 2_000): Promise<FullMenuPageData> {
    const key = `getMenuCached:${restaurantId}`;
    return getCached(key, () => menuService.fetchFullMenu(restaurantId), ttlMs);
  },

  /**
   * Upsert (create or overwrite) an entire menu for restaurant staff.
   *
   * This will:
   *  1. delete any old docs not present in the new payload
   *  2. create/update all the baseItems, categories, items, and bundles
   */
  async upsertFullMenu(
    restaurantId: string,
    fullMenu: FullMenuPageData
  ): Promise<void> {
    const root = doc(firestore, "restaurants", restaurantId);
    const batch = writeBatch(firestore);

    // helper to sync one sub‐collection
    async function syncCollection<T extends { id: string }>(
      key: keyof typeof MENU_SUBCOLS,
      entries: T[]
    ) {
      const colRef = collection(root, MENU_SUBCOLS[key]);
      const existing = await getDocs(colRef);
      const existingIds = new Set(existing.docs.map(d => d.id));

      // delete old
      for (const oldDoc of existing.docs) {
        if (!entries.find(e => e.id === oldDoc.id)) {
          batch.delete(doc(colRef, oldDoc.id));
        }
      }

      // upsert new
      for (const entry of entries) {
        const docRef = doc(colRef, entry.id);
        batch.set(docRef, entry, { merge: true });
        existingIds.delete(entry.id);
      }
    }

    // 1. baseItems
    await syncCollection("baseItems", fullMenu.baseItems);

    // 2. menuCategories
    await syncCollection("categories", fullMenu.menu.categories);

    // 3. bundles
    await syncCollection("bundles", fullMenu.menu.bundles);

    // finally commit all deletes + upserts in one go
    await batch.commit();
  },
};
